import { useEffect, useState } from "react";

import dayjs from "dayjs";

import { FaDatabase } from "react-icons/fa";
import { BsSearch } from "react-icons/bs";
import { TbReportSearch } from "react-icons/tb";
import { RiFileExcel2Fill } from "react-icons/ri";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import Col from "react-bootstrap/Col";
import InputGroup from "react-bootstrap/InputGroup";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";

import { useLayout } from "@/../_metronic/layout/core";
import { useEsus } from "@/hooks/useEsus";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { useDebounce } from "@/hooks/useDebounce";
import { getCiaps } from "@/services/esus/ciapService";
import { getCids } from "@/services/esus/cidService";
import { getProblemsPaginated } from "@/services/esus/problemService";
import { formatIdentifier } from "@/utils/formatIdentifier";
import { exportExcel } from "@/utils/exports";
import { Paginated } from "@/@types/generics/paginated";
import { GenericObject, SelectOptionsProps } from "@/@types/generics/genericals";
import { Option } from "@/@types/generics/Option";
import { Cid } from "@/@types/esus/Cid";
import { Ciap } from "@/@types/esus/Ciap";
import { Problem } from "@/@types/esus/Problem";

import { TableSkeletons } from "@/components/TableSkeletons";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { UnitsSelect } from "@/components/UnitsSelect";

import { LoadingScreen } from "@/components/LoadingScreen";
import { GenderSelect } from "@/components/GenderSelect";
import { ProfessionalsSelect } from "@/components/ProfessionalsSelect";
import { SelectComponent } from "@/components/SelectComponent";
import { ProfessionalCategorySelect } from "@/components/ProfessionalCategorySelect";

import "./styles.scss";

export function AttendanceReport() {
	const { setTitle } = useLayout();
	const { professionals, units } = useEsus();
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const [isLoading, setIsloading] = useState(false);
	const [isloadingExport, setIsLoadingExport] = useState(false);
	const [isLoadingCids, setIsLoadingCids] = useState(false);
	const [isLoadingCiaps, setIsLoadingCiaps] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const [cids, setCids] = useState<Cid[]>([]);
	const [ciaps, setCiaps] = useState<Ciap[]>([]);
	const [searchCid, setSearchCid] = useState("");
	const debouncedSearchCid = useDebounce(searchCid, 1500);
	const [searchCiap, setSearchCiap] = useState("");
	const debouncedSearchCiap = useDebounce(searchCiap, 1500);
	const [selectedOptionStartDate, setSelectedOptionStartDate] = useState(
		dayjs().subtract(1, "month").format("YYYY-MM-DD")
	);
	const [selectedOptionEndDate, setSelectedOptionEndDate] = useState(
		dayjs().format("YYYY-MM-DD")
	);
	const [paginatedProblems, setPaginatedProblems] = useState<Paginated<Problem>>(
		{} as Paginated<Problem>
	);
	const [selectedOptionUnit, setSelectedOptionUnit] = useState<SelectOptionsProps>(
		{} as SelectOptionsProps
	);
	const [selectedOptionGender, setSelectedOptionGender] = useState<SelectOptionsProps>(
		{} as SelectOptionsProps
	);
	const [selectedOptionCid, setSelectedOptionCid] = useState<Option<string>>(
		{} as Option<string>
	);
	const [selectedOptionCiap, setSelectedOptionCiap] = useState<SelectOptionsProps>(
		{} as SelectOptionsProps
	);
	const [selectedOptionProfessional, setSelectedOptionProfessional] =
		useState<SelectOptionsProps>({} as SelectOptionsProps);
	const [selectedOptionYoungerAge, setSelectedOptionYoungerAge] = useState<number>({} as number);
	const [selectedOptionOlderAge, setSelectedOptionOlderAge] = useState<number>({} as number);
	const [selectedProfessionalCategoryModal, setSelectedProfessionalCategoryModal] = useState<
		Option<string>
	>({ value: "", label: "Selecione..." } as Option<string>);

	const genderOptions = [
		{ value: "", label: "Todos" },
		{ value: "m", label: "Masculino" },
		{ value: "f", label: "Feminino" },
		{ value: "i", label: "Ignorado" },
	];

	const ciapsOptions = [
		{ value: "", label: "TODOS OS CIAPS" },
		...ciaps?.map((ciap) => ({ value: ciap.ciapCode, label: ciap.description })),
	];

	const cidsOptions = [
		{ value: "", label: "TODOS OS CIDS" },
		...cids?.map((cid) => ({ value: cid.cidCode, label: cid.description })),
	];

	async function handleCids(search?: string) {
		if (!search || !ibgeCode) return;
		setIsLoadingCids(true);
		setCids(
			await getCids({
				uf,
				ibgeCode,
				cnes,
				search: searchCid,
			})
		);
		setIsLoadingCids(false);
		setSelectedOptionCiap({} as SelectOptionsProps);
	}

	async function handleCiaps(search?: string) {
		if (!search || !ibgeCode) return;
		setIsLoadingCiaps(true);
		setCiaps(await getCiaps({ uf, ibgeCode, cnes: cnes, search: searchCiap }));
		setIsLoadingCiaps(false);
	}

	async function fetchProblems(pageSize?: number) {
		if (!(selectedOptionStartDate && selectedOptionEndDate && ibgeCode)) {
			return {} as Paginated<Problem>;
		}
		return await getProblemsPaginated({
			uf,
			ibgeCode,
			cnes: cnes || selectedOptionUnit.value,
			cids: selectedOptionCid.value ? [selectedOptionCid.value] : null,
			ciaps: selectedOptionCiap.value ? [selectedOptionCiap.value] : null,
			youngerAge: selectedOptionYoungerAge,
			olderAge: selectedOptionOlderAge,
			startDate: selectedOptionStartDate,
			endDate: selectedOptionEndDate,
			cns: selectedOptionProfessional.value,
			gender: selectedOptionGender.value,
			professionalCategory: selectedProfessionalCategoryModal.value,
			pageNumber: pageNumber,
			pageSize: pageSize,
		});
	}

	async function fetch() {
		setIsloading(true);
		setPaginatedProblems(await fetchProblems());
		setIsloading(false);
	}

	async function handleExportTable() {
		setIsLoadingExport(true);
		const response = await fetchProblems(paginatedProblems?.totalRecords);
		setIsLoadingExport(false);

		if (Object.keys(response?.data || []).length) {
			const excelData: GenericObject[] = transformToExportExcel(response?.data);
			exportExcel(excelData, `Relatorio de atendimento`);
		}
	}

	function transformToExportExcel(data: Problem[]) {
		return data.map(
			(problem) =>
				({
					"Data do atendimento": problem.attendance?.date,
					Nome: problem.citizen?.fullName,
					"Data de Nascimento": dayjs(problem.citizen?.birthdate).format("DD/MM/YYYY"),
					Cpf: problem.citizen?.cpf,
					Cns: problem.citizen?.cns,
					profissional: problem.professional1?.name,
					categoria: problem.cbo1?.description,
					cid: problem.cid?.cidCode,
					"cid-description": problem.cid?.description,
					ciap: problem.ciap?.ciapCode,
					"ciap-description": problem.ciap?.description,
				} as GenericObject)
		);
	}

	function handleChangePageNumber(newPage: number) {
		setPageNumber(newPage);
	}

	useEffect(() => {
		handleCiaps(searchCiap);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearchCiap]);

	useEffect(() => {
		handleCids(searchCid);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearchCid]);

	useEffect(() => {
		if (selectedOptionStartDate && selectedOptionEndDate) {
			fetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageNumber]);

	useEffect(() => {
		setTitle("RELATÓRIO DE ATENDIMENTOS CONSOLIDADOS");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="px-2 pb-2">
			<LoadingScreen loading={isloadingExport} />
			<Form
				className="py-2 bg-white"
				onSubmit={(event) => {
					event.preventDefault();
					setPageNumber(1);
					fetch();
				}}
			>
				<Row className="mx-2 gap-2">
					<Col sm={5} md={4} lg={4} xl={4} className="d-flex flex-column gap-2 p-0">
						<label>Período</label>
						<div className="d-flex gap-1">
							<InputGroup>
								<Form.Control
									type="date"
									className="fs-8 p-2"
									placeholder="Data inicial"
									defaultValue={dayjs().subtract(1, "month").format("YYYY-MM-DD")}
									min={dayjs().subtract(2, "years").format("YYYY-MM-DD")}
									max={dayjs().format("YYYY-MM-DD")}
									onChange={(event) =>
										setSelectedOptionStartDate(event.target.value)
									}
									required
								/>
							</InputGroup>
							<InputGroup>
								<Form.Control
									type="date"
									className="fs-8 p-2"
									placeholder="data final"
									defaultValue={dayjs().format("YYYY-MM-DD")}
									min={selectedOptionStartDate}
									max={dayjs().format("YYYY-MM-DD")}
									onChange={(event) =>
										setSelectedOptionEndDate(event.target.value)
									}
									required
								/>
							</InputGroup>
						</div>
					</Col>
					<Col sm={3} md={4} lg={4} xl={3} className="p-0">
						<UnitsSelect
							units={units}
							onChange={(event) => setSelectedOptionUnit(event as Option<string>)}
							defaultValue={{ value: "", label: "selecione..." }}
							noOptionsMessage={() => "Nenhuma unidade encontrada"}
						/>
					</Col>
					<Col sm={3} md={3} lg={3} xl={2} className="p-0">
						<ProfessionalsSelect
							professionals={professionals}
							onChange={(event) =>
								setSelectedOptionProfessional(event as Option<string>)
							}
							defaultValue={{ value: "", label: "selecione..." }}
						/>
					</Col>
					<Col sm={3} md={3} lg={3} xl={2} className="p-0 d-flex flex-column gap-2">
						<GenderSelect
							name="gender"
							options={genderOptions}
							onChange={(event) => setSelectedOptionGender(event as Option<string>)}
							defaultValue={{ value: "", label: "selecione..." }}
						/>
					</Col>
					<Col sm={2} md={2} lg={2} xl={2} className="d-flex flex-column gap-2 p-0">
						<label>Faixa etária:</label>
						<InputGroup className="gap-1">
							<Form.Control
								type="number"
								className="fs-7 p-2"
								placeholder="mín"
								onChange={(event) =>
									setSelectedOptionYoungerAge(Number(event.target.value))
								}
							/>
							<Form.Control
								type="number"
								className="fs-7 p-2"
								placeholder="max"
								onChange={(event) =>
									setSelectedOptionOlderAge(Number(event.target.value))
								}
							/>
						</InputGroup>
					</Col>
					<Col sm={3} md={3} lg={3} xl={3} className="d-flex align-items-end p-0">
						<InputGroup className="d-flex flex-column gap-2">
							<label>CIAP</label>
							<SelectComponent
								className="w-100 fs-8"
								name="ciap"
								isLoading={isLoadingCiaps}
								inputValue={searchCiap}
								onInputChange={(value) => setSearchCiap(value)}
								options={ciapsOptions}
								onChange={(event) => setSelectedOptionCiap(event as Option<string>)}
								defaultValue={{ value: "", label: "Pesquisar CIAP" }}
								noOptionsMessage={() => "nenhum resultado encontrado na pesquisa"}
							/>
						</InputGroup>
					</Col>

					<Col sm={3} md={3} lg={3} xl={3} className="d-flex align-items-end p-0">
						<InputGroup className="d-flex flex-column gap-2">
							<label>CID</label>
							<SelectComponent
								className="w-100 fs-8"
								name="cid"
								isLoading={isLoadingCids}
								inputValue={searchCid}
								options={cidsOptions}
								onInputChange={(value) => setSearchCid(value)}
								onChange={(event) => setSelectedOptionCid(event as Option<string>)}
								defaultValue={{ value: "", label: "Pesquisar CID" }}
								noOptionsMessage={() => "nenhum resultado encontrado na pesquisa"}
							/>
						</InputGroup>
					</Col>
					<Col>
						<ProfessionalCategorySelect
							onChange={(event) => {
								setSelectedProfessionalCategoryModal(event as Option<string>);
							}}
							noOptionsMessage={() => "Nenhum categoria profissional encontrada"}
						/>
					</Col>
					<Col
						sm={4}
						md={3}
						lg={3}
						xl={3}
						className="d-flex align-items-end justify-content-center gap-2"
					>
						<Button
							className="fs-8 p-3 text-nowrap w-100"
							variant="secondary"
							onClick={() =>
								paginatedProblems?.data &&
								paginatedProblems.data.length > 0 &&
								handleExportTable()
							}
						>
							<RiFileExcel2Fill />
							Exportar
						</Button>
						<Button type="submit" className="fs-8 p-3 text-nowrap w-100">
							<BsSearch className="me-2" />
							Consultar
						</Button>
					</Col>
				</Row>
			</Form>
			<Card className="my-3 bg-white">
				<Card.Title className="d-flex align-items-center gap-2 p-2 text-primary mb-0">
					<TbReportSearch size={30} className="bg-primary text-white rounded p-1 me-2" />
					Tabela relacionada
				</Card.Title>
			</Card>
			<Card className="bg-white">
				<Card.Body className="p-2">
					<Table className="bg-white" striped bordered hover responsive>
						<thead className="text-center fs-7">
							<tr className="fw-bolder">
								<th className="px-2">Atendimento</th>
								<th>Nome cidadão</th>
								<th className="text-nowrap">CPF</th>
								<th>DN</th>
								<th>Profissional</th>
								<th>Categoria</th>
								<th>CID</th>
								<th>CIAP</th>
							</tr>
						</thead>
						<tbody className="fs-8">
							{paginatedProblems?.data && !isLoading
								? paginatedProblems?.data?.map((problem, index) => (
										<tr key={`item-epidemic-data-${index}`}>
											<td className="px-2">
												{problem.attendance?.date &&
													dayjs(problem.attendance?.date).format(
														"DD/MM/YYYY"
													)}
											</td>
											<td>{problem.citizen?.fullName}</td>
											<td className="text-nowrap">
												{problem.citizen?.cpf &&
													formatIdentifier(problem.citizen?.cpf)}
											</td>
											<td>
												{problem.citizen?.birthdate &&
													dayjs(problem.citizen?.birthdate).format(
														"DD/MM/YYYY"
													)}
											</td>
											<td>{problem.professional1?.name}</td>
											<td>{problem.cbo1?.description}</td>
											<td>{problem.cid?.description}</td>
											<td className="pe-3">{problem.ciap?.description}</td>
										</tr>
								  ))
								: isLoading && <TableSkeletons numberOfCells={8} />}
						</tbody>
					</Table>
				</Card.Body>
				<Card.Footer>
					{paginatedProblems?.totalRecords ? (
						<PaginationLinks
							itemsPerPage={paginatedProblems.pageSize}
							totalPages={paginatedProblems.totalRecords}
							changeSelectedPage={handleChangePageNumber}
							pageNumber={pageNumber || 0}
						/>
					) : (
						!isLoading && (
							<NoRecordsFeedback
								message={"Nenhum resultado encontrado."}
								icon={<FaDatabase />}
							/>
						)
					)}
				</Card.Footer>
			</Card>
		</div>
	);
}
